export type MetaUpgradeId = 'maxHp' | 'startingDamage' | 'moveSpeed' | 'startingLevel';

export interface MetaUpgradeLevels {
  maxHp: number;
  startingDamage: number;
  moveSpeed: number;
  startingLevel: number;
}

interface MetaProgressData {
  coins: number;
  upgrades: MetaUpgradeLevels;
}

export interface AppliedMetaBonuses {
  maxHpMultiplier: number;
  startingDamageMultiplier: number;
  moveSpeedMultiplier: number;
  extraStartingLevels: number;
}

export const META_PROGRESSION_STORAGE_KEY = 'henry_meta_progression';
const MAX_UPGRADE_LEVEL = 5;
const UPGRADE_COSTS = [10, 25, 50, 100, 200];

const DEFAULT_DATA: MetaProgressData = {
  coins: 0,
  upgrades: {
    maxHp: 0,
    startingDamage: 0,
    moveSpeed: 0,
    startingLevel: 0,
  },
};

export class MetaProgression {
  private data: MetaProgressData;

  constructor() {
    this.data = this.load();
  }

  getCoins(): number {
    return this.data.coins;
  }

  getUpgradeLevels(): MetaUpgradeLevels {
    return { ...this.data.upgrades };
  }

  getUpgradeLevel(id: MetaUpgradeId): number {
    return this.data.upgrades[id];
  }

  getUpgradeCost(id: MetaUpgradeId): number | null {
    const currentLevel = this.getUpgradeLevel(id);
    if (currentLevel >= MAX_UPGRADE_LEVEL) return null;
    return UPGRADE_COSTS[currentLevel];
  }

  canBuyUpgrade(id: MetaUpgradeId): boolean {
    const cost = this.getUpgradeCost(id);
    return cost !== null && this.data.coins >= cost;
  }

  buyUpgrade(id: MetaUpgradeId): boolean {
    const cost = this.getUpgradeCost(id);
    if (cost === null || this.data.coins < cost) return false;

    this.data.coins -= cost;
    this.data.upgrades[id] += 1;
    this.save();
    return true;
  }

  addCoins(amount: number): void {
    if (amount <= 0) return;
    this.data.coins += Math.floor(amount);
    this.save();
  }

  spendCoins(amount: number): boolean {
    const safeAmount = Math.floor(amount);
    if (safeAmount <= 0 || this.data.coins < safeAmount) return false;
    this.data.coins -= safeAmount;
    this.save();
    return true;
  }

  getAppliedBonuses(): AppliedMetaBonuses {
    return {
      maxHpMultiplier: 1 + this.data.upgrades.maxHp * 0.1,
      startingDamageMultiplier: 1 + this.data.upgrades.startingDamage * 0.05,
      moveSpeedMultiplier: 1 + this.data.upgrades.moveSpeed * 0.03,
      extraStartingLevels: this.data.upgrades.startingLevel,
    };
  }

  private load(): MetaProgressData {
    try {
      const raw = localStorage.getItem(META_PROGRESSION_STORAGE_KEY);
      if (!raw) return { ...DEFAULT_DATA, upgrades: { ...DEFAULT_DATA.upgrades } };
      const parsed = JSON.parse(raw) as Partial<MetaProgressData>;
      const coins = Math.max(0, Math.floor(parsed.coins ?? 0));
      const upgrades: Partial<MetaUpgradeLevels> = parsed.upgrades ?? {};
      return {
        coins,
        upgrades: {
          maxHp: this.clampLevel(upgrades.maxHp),
          startingDamage: this.clampLevel(upgrades.startingDamage),
          moveSpeed: this.clampLevel(upgrades.moveSpeed),
          startingLevel: this.clampLevel(upgrades.startingLevel),
        },
      };
    } catch {
      return { ...DEFAULT_DATA, upgrades: { ...DEFAULT_DATA.upgrades } };
    }
  }

  private save(): void {
    localStorage.setItem(META_PROGRESSION_STORAGE_KEY, JSON.stringify(this.data));
  }

  private clampLevel(value: number | undefined): number {
    const safe = Math.floor(value ?? 0);
    return Math.min(MAX_UPGRADE_LEVEL, Math.max(0, safe));
  }
}

let metaProgressionInstance: MetaProgression | null = null;

export function getMetaProgression(): MetaProgression {
  if (!metaProgressionInstance) {
    metaProgressionInstance = new MetaProgression();
  }
  return metaProgressionInstance;
}
